
import MapOverlay from '@/components/MapOverlay.vue'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Action, Getter } from 'vuex-class'
import addDays from 'date-fns/esm/addDays'
import format from 'date-fns/esm/format'
import parse from 'date-fns/esm/parse'
import { Estacion } from '@/store/modules/estaciones/types'
import { GoogleMapMarker, InfoView } from '@/store/modules/google-maps/types'
import DesktopFooter from '@/components/DesktopFooter.vue'
import { getEnvironments } from '@/environments'
const env = getEnvironments()

const MODULE_ESTACION = 'estaciones'
const GOOGLE_MAPS_MODULE = 'googleMaps'

@Component({
  components: {
    MapOverlay,
    DesktopFooter
  }
})
export default class Estaciones extends Vue {
  @Prop() private grupotipovariable!: string
  @Prop() private tipovariable!: string
  @Prop() private estacion!: string

  @Action('getGrupoTipoVariable', { namespace: MODULE_ESTACION })
  private getGrupoTipoVariable: any
  @Action('getTipoVariable', { namespace: MODULE_ESTACION })
  private getTipoVariable: any
  @Action('getEstaciones', { namespace: MODULE_ESTACION })
  private getEstaciones: any
  @Action('getDataEstacion', { namespace: MODULE_ESTACION })
  private getDataEstacion: any
  @Action('resetDataEstacion', { namespace: MODULE_ESTACION })
  private resetDataEstacion: any
  @Action('getDataEstacionFromGrupoTipoVariable', { namespace: MODULE_ESTACION })
  private getDataEstacionFromGrupoTipoVariable: any
  @Action('setMarkers', { namespace: GOOGLE_MAPS_MODULE })
  private setMarkers: any
  @Action('setPosition', { namespace: GOOGLE_MAPS_MODULE })
  private setPosition: any
  @Action('setInfoViewData', { namespace: GOOGLE_MAPS_MODULE })
  private setInfoViewData: any
  @Action('setInfoViewStatus', { namespace: GOOGLE_MAPS_MODULE })
  private setInfoViewStatus: any
  @Action('resetInfoView', { namespace: GOOGLE_MAPS_MODULE })
  private resetInfoView: any
  @Action('setAutocomplete', { namespace: GOOGLE_MAPS_MODULE })
  private setAutocomplete: any

  @Getter('getEstacionesTipoVariable', { namespace: MODULE_ESTACION })
  private estacionesTipoVariable!: Estacion[]
  @Getter('getEstaciones', { namespace: MODULE_ESTACION })
  private estaciones!: Estacion[]
  @Getter('getEstacion', { namespace: MODULE_ESTACION })
  private getEstacion!: any
  @Getter('markers', { namespace: GOOGLE_MAPS_MODULE })
  private markers!: GoogleMapMarker[]
  @Getter('infoView', { namespace: GOOGLE_MAPS_MODULE })
  private infoView!: InfoView
  @Getter('activeMarker', { namespace: GOOGLE_MAPS_MODULE })
  private activeMarker!: GoogleMapMarker

  async created () {
    await this.getGrupoTipoVariable()
    await this.grupoTipoVariableChanged(this.grupotipovariable)
    await this.tipoVariableChanged(this.tipovariable)
    this.estacionChanged(Number(this.estacion))
  }

  mounted () {
    this.setAutocomplete({ shown: true })
  }

  customGetTipoVariable (grupoTipoVariable: string) {
    if (grupoTipoVariable) {
      return this.getTipoVariable(grupoTipoVariable)
    }
    this.setMarkers([])
  }

  customGetEstaciones (tipoVariable: string) {
    if (tipoVariable) {
      return this.getEstaciones(tipoVariable)
    } else {
      this.setInfoViewStatus({ active: false, selectedMarker: -1 })
      return this.customGetTipoVariable(this.grupotipovariable)
    }
  }

  @Watch('grupotipovariable')
  grupoTipoVariableChanged (grupoTipoVariable: string) {
    this.setInfoViewStatus({ active: false, selectedMarker: undefined })
    return this.customGetTipoVariable(grupoTipoVariable)
  }

  @Watch('tipovariable')
  tipoVariableChanged (tipovariable: string) {
    this.setInfoViewStatus({ active: false, selectedMarker: undefined })
    return this.customGetEstaciones(tipovariable)
  }

  @Watch('estacion')
  async estacionChanged (estacion: number) {
    await this.setInfoViewStatus({ active: false, selectedMarker: undefined })
    if (estacion) {
      const marker = (this.markers.filter(marker => marker.id === estacion))[0]
      if (marker) {
        const position = marker.position
        const zoom = env.settings.ESTACIONES.zoom
        this.setInfoViewData({ position })
        this.setInfoViewStatus({ active: true, selectedMarker: marker.id })
      }
    } else {
      this.setInfoViewStatus({ active: false, selectedMarker: undefined })
      this.customGetEstaciones(this.tipovariable)
    }
  }

  @Watch('estacionesTipoVariable')
  estacionesTipoVariableChanged (estaciones: Estacion[]) {
    if (estaciones) {
      this.setMarkers(this.format(estaciones))
    }
  }

  @Watch('estaciones')
  estacionesChanged (estaciones: Estacion[]) {
    if (estaciones) {
      this.setMarkers(this.format(estaciones))
    }
  }

  format (estaciones: Estacion[]) {
    if (estaciones) {
      return estaciones.map((estacion) => {
        return {
          id: estacion.id,
          position: {
            lat: estacion.latitud,
            lng: estacion.longitud
          },
          params: estacion
        }
      })
    } else {
      return []
    }
  }

  @Watch('infoView.status.active')
  async infoViewChanged (active: boolean) {
    if (active) {
      this.setInfoViewLocalData({
        grupoTipoVariable: this.grupotipovariable,
        tipoVariable: this.tipovariable,
        estacion: this.estacion
      })
      const periodo = {
        inicio: format(addDays(Date.now(), -6), 'yyyy-MM-dd'),
        fin: format(Date.now(), 'yyyy-MM-dd')
      }
      const { id, params } = this.activeMarker
      this.setInfoViewData({
        title: params.nombre,
        subtitle: 'Últimos 7 días',
        emptyResponse: this.tipovariable && this.tipovariable !== '' ? 'Origen sin datos.' : 'Seleccione una variable',
        data: [
          {
            datos: [{
              nombre: '',
              titulo: '',
              valor_list: [
                {
                  tipo: 'institución',
                  valor: params.institucion.nombre
                },
                {
                  tipo: 'identificador',
                  valor: params.identificador
                },
                {
                  tipo: 'latitud',
                  valor: params.latitud
                },
                {
                  tipo: 'longitud',
                  valor: params.longitud
                }
              ]
            }]
          }
        ]
      })
    }
  }

  chartFormat (dataEstacion: Array<{ valor: string, fecha: string}>) {
    const data = dataEstacion
      .map(item => ([Date.parse(parse(item.fecha, 'yyyy-MM-dd H:mm:ss', new Date()).toString()), parseFloat(item.valor)]))
    return (data.length === 0) ? null : {
      series: [{
        type: 'line',
        name: 'dato',
        data
      }]
    }
  }

  async setInfoViewLocalData (data : { estacion: string, tipoVariable: string, grupoTipoVariable: string }) {
    const { estacion, tipoVariable, grupoTipoVariable } = data
    const final = Date.now()
    const init = addDays(final, -6)
    if (estacion && tipoVariable) {
      await this.getDataEstacion({
        estacion,
        variable: this.tipovariable,
        init,
        final
      })
    } else {
      await this.resetDataEstacion()
      if (grupoTipoVariable && estacion) {
        await this.getDataEstacionFromGrupoTipoVariable({ grupoTipoVariable, estacion })
      }
    }
    const chartOptions = this.chartFormat(this.getEstacion)
    this.setInfoViewData({ chartOptions: [chartOptions] })
  }

  async beforeDestroy () {
    await this.setAutocomplete({ shown: false })
    this.setMarkers([])
    this.resetInfoView()
  }
}
